<template>
  <div class="double-status-table-queue">
    <div class="double-status-table-queue__container">
      <div class="double-status-table-queue__header">
        <div class="double-status-table-queue__queue-ticket">
          <div class="double-status-table-queue__queue-ticket-list">
            <span class="double-status-table-queue__queue-ticket-list-title" v-html="$t('ticket.title')" />

            <div class="double-status-table-queue__queue-ticket-list-content" v-if="queueTickets.length > 0">
              <div
                class="double-status-table-queue__queue-ticket-list-item"
                v-for="(ticket, index) in queueTickets"
                :key="index"
              >
                <div class="double-status-table-queue__queue-ticket-item">
                  {{ ticket.ticketNumber }}
                </div>
              </div>
            </div>

            <span v-else class="double-status-table-queue__queue-ticket-empty" v-html="$t('ticket.empty')" />
          </div>
        </div>

        <div class="double-status-table-queue__adv">
          <AdPlayList
            v-if="showAdv"
            :play-list-items="playlistMainItems"
            :full-screen="clientsEmpty"
          />

          <LogoEmpty v-else class="double-status-table-queue__logo" />
        </div>
      </div>

      <div class="double-status-table-queue__footer">
        <div class="double-status-table-queue__called-ticket">
          <div class="double-status-table-queue__called-ticket-list-content">
            <div class="double-status-table-queue__called-titles">
              <span class="double-status-table-queue__called-titles-table" v-html="$t('ticket.tableName')" />
              <span class="double-status-table-queue__called-titles-ticket" v-html="$t('ticket.ticketName')" />
            </div>

            <div
              class="double-status-table-queue__called-ticket-list-item"
              v-for="(ticket, index) in calledTickets"
              :key="index"
            >
              <div class="double-status-table-queue__called-ticket-item called-ticket-item" :class="{ 'ticket-blink': setBlink(ticket) }">
                <div class="called-ticket-item__seat-label">
                  {{ getTicketsSeatLabel(ticket) }}
                </div>

                <div class="called-ticket-item__number">
                  {{ getTicketsNumber(ticket) }}
                </div>
              </div>
          </div>
          </div>
        </div>
      </div>

      <LogoEmpty v-show="showLogoFullScreen && !showAdv" class="double-status-table-queue__logo-fullscreen" />
      <div class="table-queue__speaker">
          <PlayList/>
      </div>
    </div>
  </div>
</template>

<script>
import AdPlayList from '@/components/playlist/AdPlayList.vue'
import TableQueueController from '@/views/table/queue/table-queue-controller'
import LogoEmpty from '@/components/logos/LogoEmpty.vue'
import ScreenStateIdTypes from '@/components/tickets/model/screen-state-id-types'
import Helper from '@/utils/helper'
import { mapState } from 'vuex'
import PlayList from '@/components/playlist/PlayList'

export default {
  name: 'DoubleStatusTableQueue',
  mixins: [TableQueueController],
  components: {
    PlayList,
    LogoEmpty,
    AdPlayList
  },
  data () {
    return {
      maxTicketCalledLength: 8
    }
  },
  computed: {
    ...mapState({
      playListSpeakId: state => state.playlist.speakId
    }),
    queueTickets () {
      const isInQueueOrPostponed = ticket =>
          ScreenStateIdTypes.isInQueue(ticket.getScreenStateId()) ||
          ScreenStateIdTypes.isPostponed(ticket.getScreenStateId())

      return this.tickets.filter(isInQueueOrPostponed)
    },
    calledTickets () {
      const parseSeatLabel = label => {
        const regex = /^([A-Za-z]+)(\d+)$/
        const [, letter = '', num = '0'] = label.match(regex) || []
        return { letter, num: parseInt(num, 10) }
      }

      const compareSeats = (a, b) => {
        const aSeat = parseSeatLabel(a.getSeatLabel())
        const bSeat = parseSeatLabel(b.getSeatLabel())

        if (aSeat.letter < bSeat.letter) return -1
        if (aSeat.letter > bSeat.letter) return 1

        return aSeat.num - bSeat.num
      }

      const isCalledOrServicing = ticket =>
          ScreenStateIdTypes.isCalled(ticket.getScreenStateId()) ||
          ScreenStateIdTypes.isServicing(ticket.getScreenStateId())

      const filtered = this.tickets.filter(isCalledOrServicing).sort(compareSeats)

      // Добавляем заглушки, чтобы длина была всегда maxTicketCalledLength
      const placeholdersCount = this.maxTicketCalledLength - filtered.length

      if (placeholdersCount > 0) {
        // Создаем массив заглушек (например, null или объекты с флагом isPlaceholder)
        const placeholders = Array(placeholdersCount).fill(null)
        return [...filtered, ...placeholders]
      }

      return filtered
    }
  },
  methods: {
    getTicketsNumber (ticket) {
      return Helper.getValue(ticket?.ticketNumber, '-')
    },
    getTicketsSeatLabel (ticket) {
      return Helper.getValue(ticket?.seatLabel, '-')
    },
    setBlink (ticket) {
      return this.playListSpeakId === ticket?.id
    }
  }
}
</script>

<style lang="stylus">

.double-status-table-queue {
  display flex
  flex 1

  &__container {
    flex 1
    display flex
    flex-direction column
  }

  &__header {
    display flex
    flex-direction row
    justify-content space-between
  }

  &__footer {
    display flex
    flex 1 auto
  }

  &__queue-ticket {
    flex 30%
    display flex
    padding 50px
    box-sizing border-box

    color var(--color-text)

    &-list {
      display flex
      flex-direction column
      align-items center
      width 100%
      gap 50px

      &-title {
        font-size 42px
        text-align center
        font-weight 500
        line-height normal
        color var(--color-text)
        opacity 0.9

        @media (orientation: landscape) {
          @media (min-width: 2560px) {
            font-size 52px
          }

          @media (min-width: 3840px) {
            font-size 62px
          }
        }
      }

      &-content {
        display flex
        flex-wrap wrap
        gap 10px
        width 100%
        max-height 470px
        overflow hidden

        @media (orientation: landscape) {
          @media (min-width: 2560px) {
            max-height 692px
          }

          @media (min-width: 3840px) {
            max-height 1142px
          }
        }
      }
    }

    &-item {
      white-space nowrap
      font-size 42px
      line-height normal
      text-align center
      font-weight 500
      background-color #f8f8f8
      border 1px solid #E0E0E0
      padding 0.4em
      border-radius 0.25em
      margin-bottom 10px

      @media (orientation: landscape) {
        @media (min-width: 2560px) {
          font-size 60px
        }

        @media (min-width: 3840px) {
          font-size 90px
        }
      }
    }
  }

  &__queue-ticket-empty {
    font-size 42px
    text-align center
    font-weight 400
    line-height normal
    align-self center

    flex 1
    display flex
    justify-content center
    align-items center

    color var(--color-text)
    opacity 0.25
  }

  &__adv {
    flex 60%
    display flex
    position relative
    overflow hidden
    aspect-ratio 16/9
    border-radius 0 0 0 30px

    .add-contents {
      width 100%
      flex 0 1 auto

      .content {
        flex 1
        height 100%
        width 100%

        .vertical,
        .horizontal {
          height 100%
          width 100%
          object-fit cover
        }
      }
    }
  }

  &__called-titles {
    display flex
    flex-direction column
    justify-content space-around

    &-table,
    &-ticket {
      font-size 42px
      line-height 1em
      font-weight 500
      color var(--color-text)
      opacity 0.8

      @media (orientation: landscape) {
        @media (min-width: 2560px) {
          font-size 52px
        }

        @media (min-width: 3840px) {
          font-size 62px
        }
      }
    }
  }

  &__called-ticket {
    flex 1
    display flex
    padding 30px

    &-list {
      &-content {
        flex 1
        display flex
        flex-direction row
        justify-content space-between
        gap 30px
      }

      &-item {
        display flex
        flex 1
      }
    }

    & .called-ticket-item {
      flex 1
      display flex
      flex-direction column
      align-items center

      font-size 40px
      line-height normal
      font-weight 500

      background-color #FFFFFF
      border-radius 0.25em
      border 1px solid #E0E0E0
      box-sizing border-box
      overflow hidden
      transition all 0.5s ease

      @media (orientation: landscape) {
        @media (min-width: 2560px) {
          font-size 52px
        }

        @media (min-width: 3840px) {
          font-size 90px
        }
      }

      &__number {
        flex 1
        width 100%
        display flex
        justify-content center
        align-items center
        background-color #f8f8f8
        transition all 0.5s ease

        color var(--color-text)
      }

      &__seat-label {
        flex 1
        width 100%
        display flex
        justify-content center
        align-items center
        transition all 0.5s ease

        color var(--color-highlighty)
      }

      &.ticket-blink {
        .called-ticket-item {
          &__number,
          &__seat-label {
            color #FFFFFF
            background-color var(--color-highlighty)
          }
        }
      }
    }
  }

  &__speaker {
    position absolute
    top 50px
    right 50px
  }

  &__logo {
    flex 1
    display flex
    align-items center
    justify-content center

    .icon-empty {
      height auto

      .logo {
        width 100%
        height 100%
        aspect-ratio 1
      }
    }

    &-fullscreen {
      position absolute
      top 0
      right 0
      left 0
      bottom 0
      background-color #FFFFFF

      display flex
      align-items center
      justify-content center
    }
  }
}

</style>
